package com.fantasytenis.web.components.lineup

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Matchup difficulty levels based on ranking differential
 */
enum class MatchupDifficulty(
    val label: String,
    val backgroundColor: CSSColorValue,
    val textColor: CSSColorValue
) {
    EASY("EASY", Color("#dcfce7"), Color("#166534")),           // Green
    FAVORABLE("FAVORABLE", Color("#d1fae5"), Color("#047857")), // Light green
    EVEN("EVEN", Color("#f3f4f6"), Color("#4b5563")),           // Gray
    TOUGH("TOUGH", Color("#fef3c7"), Color("#92400e")),         // Orange/amber
    HARD("HARD", Color("#fee2e2"), Color("#991b1b"))            // Red
}

/**
 * Determines matchup difficulty based on ranking differential.
 * Positive diff means player is ranked higher (easier matchup).
 * Negative diff means opponent is ranked higher (tougher matchup).
 */
fun getMatchupDifficulty(playerRanking: Int?, opponentRanking: Int?): MatchupDifficulty? {
    if (playerRanking == null || opponentRanking == null) return null

    val diff = opponentRanking - playerRanking
    return when {
        diff > 30 -> MatchupDifficulty.EASY
        diff > 10 -> MatchupDifficulty.FAVORABLE
        diff > -10 -> MatchupDifficulty.EVEN
        diff > -30 -> MatchupDifficulty.TOUGH
        else -> MatchupDifficulty.HARD
    }
}

/**
 * Matchup Indicator Component
 *
 * Displays a visual badge showing matchup difficulty based on ranking differential.
 * Shows the rank differential number (e.g., "+14" or "-8") and a difficulty label.
 */
@Composable
fun MatchupIndicator(
    playerRanking: Int?,
    opponentRanking: Int?,
    showLabel: Boolean = true,
    compact: Boolean = false
) {
    val difficulty = getMatchupDifficulty(playerRanking, opponentRanking)

    // If we can't determine difficulty, don't show anything
    if (difficulty == null || playerRanking == null || opponentRanking == null) {
        return
    }

    val diff = opponentRanking - playerRanking
    val diffText = if (diff >= 0) "+$diff" else "$diff"

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(if (compact) 4.px else 6.px)
            backgroundColor(difficulty.backgroundColor)
            color(difficulty.textColor)
            padding(if (compact) 2.px else 4.px, if (compact) 6.px else 10.px)
            borderRadius(if (compact) 4.px else 6.px)
            fontSize(if (compact) 10.px else 12.px)
            fontWeight(600)
            whiteSpace("nowrap")
        }
    }) {
        // Rank differential
        Span({
            style {
                fontWeight(700)
            }
        }) {
            Text(diffText)
        }

        // Difficulty label
        if (showLabel && !compact) {
            Span({
                style {
                    fontSize(10.px)
                    textTransform("uppercase".unsafeCast<String>())
                    letterSpacing(0.5.px)
                }
            }) {
                Text(difficulty.label)
            }
        }
    }
}

/**
 * Simplified matchup indicator showing just the difficulty badge without numbers.
 * Useful for very compact displays.
 */
@Composable
fun MatchupBadge(
    playerRanking: Int?,
    opponentRanking: Int?
) {
    val difficulty = getMatchupDifficulty(playerRanking, opponentRanking) ?: return

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            backgroundColor(difficulty.backgroundColor)
            color(difficulty.textColor)
            padding(2.px, 6.px)
            borderRadius(4.px)
            fontSize(9.px)
            fontWeight(700)
            textTransform("uppercase".unsafeCast<String>())
            letterSpacing(0.5.px)
        }
    }) {
        Text(difficulty.label)
    }
}

private fun StyleScope.textTransform(value: String) {
    property("text-transform", value)
}
